<div class="flex gap-6 h-full">
  <!-- Sidebar nav -->
  <aside class="w-52 shrink-0">
    <div class="sticky top-0 flex flex-col gap-1 pt-1">
      <p class="text-[11px] font-bold uppercase tracking-widest text-neutral mb-2">
        Component Library
      </p>
      @for (section of sections; track section.id) {
        <button
          (click)="scrollTo(section.id)"
          class="text-left px-3 py-2 rounded-lg text-body transition-colors cursor-pointer"
          [class.bg-primary]="activeSection() === section.id"
          [class.text-white]="activeSection() === section.id"
          [class.text-neutral]="activeSection() !== section.id"
          [class.hover:bg-tertiary]="activeSection() !== section.id"
        >
          {{ section.label }}
        </button>
      }
    </div>
  </aside>

  <!-- Main content -->
  <div class="flex-1 flex flex-col gap-10 pb-10">
    <!-- Colors & Tokens -->
    <section id="section-colors">
      <h2 class="section-title">Colors & Tokens</h2>
      <p class="section-desc">
        CSS variables injected at runtime via <code>initialize-styles.helper.ts</code>. Available as
        Tailwind utilities via <code>tailwind.config.js</code>.
      </p>
      <div class="grid grid-cols-2 gap-3">
        @for (token of colorTokens; track token.name) {
          <div class="token-card">
            <div class="h-12 rounded-lg mb-3" [style.background]="token.hex"></div>
            <p class="text-body font-semibold">{{ token.label }}</p>
            <p class="text-[11px] text-neutral font-mono">{{ token.name }}</p>
            <p class="text-[11px] text-neutral font-mono">{{ token.hex }}</p>
          </div>
        }
        <div class="token-card">
          <div class="h-12 rounded-lg mb-3 bg-primary" style="opacity: 0.15"></div>
          <p class="text-body font-semibold">Primary (tints)</p>
          <p class="text-[11px] text-neutral font-mono">--primary__0 … --primary__100</p>
          <p class="text-[11px] text-neutral font-mono">--primary_hover, --secondary_hover</p>
        </div>
      </div>
    </section>

    <mat-divider></mat-divider>

    <!-- Typography -->
    <section id="section-typography">
      <h2 class="section-title">Typography</h2>
      <p class="section-desc">
        Tokens defined in <code>tailwind.config.js</code>. All 257 arbitrary bracket occurrences
        replaced.
      </p>
      <div class="flex flex-col gap-4 mt-4">
        <div class="type-row">
          <span class="type-label">text-[10px] / text-xs</span>
          <span style="font-size: 10px">The quick brown fox — stepper labels, radio buttons</span>
        </div>
        <div class="type-row">
          <span class="type-label">text-[12px] / text-xs</span>
          <span style="font-size: 12px">The quick brown fox — table headers</span>
        </div>
        <div class="type-row">
          <span class="type-label">text-body (13px)</span>
          <span class="text-body"
            >The quick brown fox — primary body text, inputs, table cells</span
          >
        </div>
        <div class="type-row">
          <span class="type-label">text-body-md (15px)</span>
          <span class="text-body-md">The quick brown fox — medium emphasis text</span>
        </div>
        <div class="type-row">
          <span class="type-label">text-body-lg (17px)</span>
          <span class="text-body-lg">The quick brown fox — section titles</span>
        </div>
        <div class="type-row">
          <span class="type-label">text-xl (20px)</span>
          <span class="text-xl">The quick brown fox — dialog titles</span>
        </div>
        <div class="type-row">
          <span class="type-label">text-2xl (24px)</span>
          <span class="text-2xl font-semibold">Page heading</span>
        </div>
      </div>
    </section>

    <mat-divider></mat-divider>

    <!-- Buttons -->
    <section id="section-buttons">
      <h2 class="section-title">Buttons</h2>
      <p class="section-desc">All Angular Material button variants used in this app.</p>

      <div class="flex flex-col gap-6">
        <div>
          <p class="subsection-label">xemex-filled-button</p>
          <div class="flex flex-wrap gap-3 mt-2">
            <xemex-filled-button>Primary</xemex-filled-button>
            <xemex-filled-button intent="destructive">Destructive</xemex-filled-button>
            <xemex-filled-button [disabled]="true">Disabled</xemex-filled-button>
          </div>
        </div>
        <div>
          <p class="subsection-label">xemex-outlined-button</p>
          <div class="flex flex-wrap gap-3 mt-2">
            <xemex-outlined-button>Default (cancel)</xemex-outlined-button>
            <xemex-outlined-button intent="destructive">Destructive</xemex-outlined-button>
            <xemex-outlined-button [disabled]="true">Disabled</xemex-outlined-button>
          </div>
        </div>
        <div>
          <p class="subsection-label">mat-icon-button</p>
          <div class="flex flex-wrap gap-3 mt-2 items-center">
            <button mat-icon-button color="primary"><mat-icon>edit</mat-icon></button>
            <button mat-icon-button color="secondary"><mat-icon>close</mat-icon></button>
            <button mat-icon-button color="warn"><mat-icon>delete</mat-icon></button>
            <button mat-icon-button><mat-icon>more_vert</mat-icon></button>
          </div>
        </div>
        <div class="p-4 bg-amber-50 border border-amber-200 rounded-xl text-body">
          <strong>Inconsistency:</strong> Confirm dialogs use
          <code>color="secondary"</code> (orange) for most actions but <code>color="warn"</code> for
          destructive ones — applied inconsistently. Standardize: use <code>warn</code> for all
          destructive confirms, <code>secondary</code> for neutral confirms.
        </div>
      </div>
    </section>

    <mat-divider></mat-divider>

    <!-- Status Badges -->
    <section id="section-status">
      <h2 class="section-title">Status Badges</h2>
      <p class="section-desc">
        <span class="badge badge-warn">Previously 5 implementations</span> Now consolidated into
        <code>xemex-status-badge</code> — used via
        <code>[status]="'success' | 'error' | 'inactive' | 'warning'"</code>.
      </p>

      <div class="flex flex-col gap-4 mt-4">
        <div>
          <p class="subsection-label">Previous implementations (inline Tailwind — now replaced)</p>
          <div class="flex flex-wrap gap-6 mt-3">
            <div class="flex items-center gap-2">
              <div class="bg-[#00C853] rounded-full h-3 w-3"></div>
              <span class="text-body">Active (old — inline Tailwind)</span>
            </div>
            <div class="status-indicator active flex items-center gap-2">
              <div class="dot"></div>
              <span class="text-body">Active (old — SCSS class)</span>
            </div>
          </div>
        </div>

        <div>
          <p class="subsection-label">Live xemex-status-chip variants</p>
          <div class="flex flex-wrap gap-4 mt-3">
            <xemex-status-chip status="success" label="success" />
            <xemex-status-chip status="pending" label="pending" />
            <xemex-status-chip status="warning" label="warning" />
            <xemex-status-chip status="error" label="error" />
            <xemex-status-chip status="inactive" label="inactive" />
          </div>
        </div>
      </div>
    </section>

    <mat-divider></mat-divider>

    <!-- Surfaces & Borders -->
    <section id="section-surfaces">
      <h2 class="section-title">Surfaces & Borders</h2>
      <p class="section-desc">
        <span class="badge badge-warn">10 surface values</span>
        <span class="badge badge-warn ml-2">5 border values</span>
        All mean the same thing — no shared token.
      </p>

      <div class="flex flex-col gap-6 mt-4">
        <div>
          <p class="subsection-label">Surface backgrounds (all used for "card/container")</p>
          <div class="flex flex-wrap gap-2 mt-2">
            @for (color of inconsistentSurfaces; track color) {
              <div class="flex flex-col items-center gap-1">
                <div
                  class="w-16 h-10 rounded-lg border border-border-card"
                  [style.background]="color"
                ></div>
                <span class="text-[10px] font-mono text-neutral">{{ color }}</span>
              </div>
            }
          </div>
          <div class="mt-3 p-3 bg-green-50 border border-green-200 rounded-xl text-body">
            <strong>Fix:</strong> Use <code>bg-surface</code> (= <code>var(--tertiary)</code> =
            <code>#F4F4F4</code>) — token now defined in Tailwind config.
          </div>
        </div>

        <div>
          <p class="subsection-label">Border colors (all used for "card border")</p>
          <div class="flex flex-wrap gap-2 mt-2">
            @for (color of inconsistentBorders; track color) {
              <div class="flex flex-col items-center gap-1">
                <div class="w-16 h-10 rounded-lg border-2" [style.border-color]="color"></div>
                <span class="text-[10px] font-mono text-neutral">{{ color }}</span>
              </div>
            }
          </div>
          <div class="mt-3 p-3 bg-green-50 border border-green-200 rounded-xl text-body">
            <strong>Fix:</strong> Use <code>border-border-card</code> everywhere — token now defined
            in Tailwind config.
          </div>
        </div>
      </div>
    </section>

    <mat-divider></mat-divider>

    <!-- Spinner -->
    <section id="section-spinner">
      <h2 class="section-title">Spinner</h2>
      <p class="section-desc">
        <code>xemex-spinner</code> exists but <code>mat-progress-spinner</code> /
        <code>mat-spinner</code> are used directly in
        <span class="badge badge-warn">18+ templates</span> with 5 different diameters.
      </p>
      <div class="flex gap-10 items-center mt-4">
        <div class="flex flex-col items-center gap-2">
          <xemex-spinner></xemex-spinner>
          <span class="text-[12px] text-neutral">xemex-spinner</span>
        </div>
      </div>
      <div class="mt-4 p-3 bg-green-50 border border-green-200 rounded-xl text-body">
        <strong>Fix:</strong> Replace all <code>mat-progress-spinner</code> /
        <code>mat-spinner</code> usages with <code>&lt;xemex-spinner&gt;</code>.
      </div>
    </section>

    <mat-divider></mat-divider>

    <!-- Skeleton -->
    <section id="section-skeleton">
      <h2 class="section-title">Skeleton Block</h2>
      <p class="section-desc">
        Most-used shared component — 344 usages. Inputs: <code>width</code>, <code>height</code>,
        <code>color</code>, <code>borderRadius</code>.
      </p>
      <div class="flex flex-col gap-3 mt-4 max-w-md">
        <xemex-skeleton-block width="100%" height="20px"></xemex-skeleton-block>
        <xemex-skeleton-block width="75%" height="20px"></xemex-skeleton-block>
        <xemex-skeleton-block width="50%" height="20px"></xemex-skeleton-block>
        <xemex-skeleton-block width="100%" height="40px" borderRadius="8px"></xemex-skeleton-block>
        <xemex-skeleton-block width="60px" height="60px" borderRadius="50%"></xemex-skeleton-block>
      </div>
    </section>

    <mat-divider></mat-divider>

    <!-- No Items Found -->
    <section id="section-no-items">
      <h2 class="section-title">No Items Found</h2>
      <p class="section-desc">
        Input: <code>NoItemsIllustration</code> — values: <code>'loop'</code> (default),
        <code>'partner'</code>, <code>'location'</code>, <code>'device'</code>,
        <code>'activity'</code>.
      </p>
      <div class="border border-border-card rounded-xl">
        <xemex-no-items-found NoItemsIllustration="loop"></xemex-no-items-found>
      </div>
    </section>

    <mat-divider></mat-divider>

    <!-- Form Elements -->
    <section id="section-forms">
      <h2 class="section-title">Form Elements</h2>
      <p class="section-desc">
        Angular Material M3 form controls with current global overrides from
        <code>src/styles/</code>.
      </p>
      <div class="flex flex-col gap-6 mt-4 max-w-sm">
        <mat-form-field appearance="outline" class="w-full">
          <mat-label>Text input</mat-label>
          <input matInput placeholder="Placeholder text" />
        </mat-form-field>

        <mat-form-field appearance="outline" class="w-full">
          <mat-label>Select</mat-label>
          <mat-select>
            <mat-option value="1">Option 1</mat-option>
            <mat-option value="2">Option 2</mat-option>
          </mat-select>
        </mat-form-field>

        <mat-form-field appearance="outline" class="w-full">
          <mat-label>Readonly field</mat-label>
          <input matInput value="Read-only value" class="readonly-disabled" readonly />
        </mat-form-field>

        <div class="flex flex-col gap-3">
          <mat-checkbox>Checkbox unchecked</mat-checkbox>
          <mat-checkbox checked>Checkbox checked</mat-checkbox>
        </div>

        <div class="flex flex-col gap-3">
          <mat-slide-toggle>Toggle off</mat-slide-toggle>
          <mat-slide-toggle checked>Toggle on</mat-slide-toggle>
        </div>
      </div>
    </section>

    <mat-divider></mat-divider>

    <!-- Dialog Shell -->
    <section id="section-dialog-shell">
      <h2 class="section-title">Dialog Shell</h2>
      <p class="section-desc">
        Shared shell that standardizes the header + close button pattern across all dialogs.
        Eliminates <span class="badge badge-warn">20+ duplicated</span> outer wrappers.
      </p>

      <div class="flex flex-col gap-6 mt-4">
        <div>
          <p class="subsection-label">API</p>
          <div class="mt-2 rounded-xl border border-border-card overflow-hidden text-body">
            <table class="w-full">
              <thead class="bg-tertiary">
                <tr>
                  <th class="text-left px-4 py-2 font-semibold text-neutral">Input</th>
                  <th class="text-left px-4 py-2 font-semibold text-neutral">Type</th>
                  <th class="text-left px-4 py-2 font-semibold text-neutral">Required</th>
                  <th class="text-left px-4 py-2 font-semibold text-neutral">Description</th>
                </tr>
              </thead>
              <tbody>
                <tr class="border-t border-border-card">
                  <td class="px-4 py-2 font-mono">title</td>
                  <td class="px-4 py-2 font-mono text-neutral">string</td>
                  <td class="px-4 py-2">Yes</td>
                  <td class="px-4 py-2">
                    Dialog heading rendered in <code>text-2xl font-semibold</code>
                  </td>
                </tr>
                <tr class="border-t border-border-card">
                  <td class="px-4 py-2 font-mono">ng-content</td>
                  <td class="px-4 py-2 font-mono text-neutral">default slot</td>
                  <td class="px-4 py-2">—</td>
                  <td class="px-4 py-2">
                    Dialog body content projected inside the scrollable wrapper
                  </td>
                </tr>
                <tr class="border-t border-border-card">
                  <td class="px-4 py-2 font-mono">actions</td>
                  <td class="px-4 py-2 font-mono text-neutral">named slot</td>
                  <td class="px-4 py-2">—</td>
                  <td class="px-4 py-2">
                    Footer content projected outside the scroll wrapper — use
                    <code>&lt;mat-dialog-actions actions&gt;</code>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <div>
          <p class="subsection-label">Usage</p>
          <pre
            class="mt-2 p-4 bg-surface rounded-xl text-[12px] font-mono overflow-x-auto"
          ><code>&lt;xemex-dialog-shell [title]="'my.title.key' | translate"&gt;
  &lt;!-- body content --&gt;
  &lt;div class="p-2"&gt;Are you sure?&lt;/div&gt;

  &lt;mat-dialog-actions actions&gt;
    &lt;button mat-flat-button color="warn" (click)="onConfirm()"&gt;Confirm&lt;/button&gt;
    &lt;button mat-stroked-button mat-dialog-close&gt;Cancel&lt;/button&gt;
  &lt;/mat-dialog-actions&gt;
&lt;/xemex-dialog-shell&gt;</code></pre>
        </div>

        <div>
          <p class="subsection-label">Migrated dialogs</p>
          <div class="mt-2 flex flex-col gap-1 text-body">
            <div class="flex items-center gap-2">
              <mat-icon class="text-primary text-base h-5 w-5">check_circle</mat-icon>
              <span><code>delete-user-dialog</code> — companies/components</span>
            </div>
            <div class="flex items-center gap-2">
              <mat-icon class="text-primary text-base h-5 w-5">check_circle</mat-icon>
              <span><code>resend-invite-dialog</code> — companies/components</span>
            </div>
            <div class="flex items-center gap-2">
              <mat-icon class="text-primary text-base h-5 w-5">check_circle</mat-icon>
              <span><code>confirm-close-status-dialog</code> — devices/components</span>
            </div>
            <div class="flex items-center gap-2">
              <mat-icon class="text-primary text-base h-5 w-5">check_circle</mat-icon>
              <span><code>confirm-deactivate-firmware-dialog</code> — firmware/components</span>
            </div>
          </div>
        </div>

        <div>
          <p class="subsection-label">Skipped dialogs (reason)</p>
          <div class="mt-2 flex flex-col gap-1 text-body text-neutral">
            <div>
              <code>create-new-user-dialog</code> — multi-step layout with bespoke flex footer
              inside the wrapper; no <code>mat-dialog-actions</code>
            </div>
            <div>
              <code>shipment-file-detail-dialog</code> — title requires pipe interpolation (<code
                >translate:{{ '{' }} fileId {{ '}' }}</code
              >); action buttons are a plain <code>&lt;div&gt;</code> inside the scroll wrapper, not
              <code>mat-dialog-actions</code>
            </div>
            <div>
              <code>upload-shipment-file-dialog</code> — no <code>mat-dialog-actions</code>; close
              button lives inside the single <code>justify-between</code> flex wrapper
            </div>
            <div>
              <code>connection-test-dialog</code> — same pattern as upload-shipment: close inside
              main wrapper, no <code>mat-dialog-actions</code>
            </div>
          </div>
        </div>
      </div>
    </section>
  </div>
</div>
